import React, { useCallback, useReducer, useMemo } from "react"
import {
  useLoaderData,
  useRouteLoaderData,
  useFetcher,
  useNavigate,
  type ActionFunctionArgs,
  Outlet,
  useMatches,
} from "react-router"
import qs from "qs"
import { toast } from "sonner"

import { fetchAPI, handleResponse, throwIfNotOk } from "@/utils"
import { getOrganisations } from "@/utils/projects"
import { FiltersWrapper, type OnBeforeSubmit, type StoredSession } from "@/components/Filters"
import type { RootLoader } from "@/routes/root"
import type {
  ActionResponse,
  DocumentClassificationType,
  DocumentEvidenceCategory,
  EvidenceTheme,
  Project,
} from "@/utils/types"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { PopoverClose } from "@radix-ui/react-popover"
import { LoaderCircleIcon, SaveIcon, PlusIcon, SettingsIcon, XCircleIcon, TrashIcon, Pencil } from "lucide-react"

import { RecordListTab } from "./recordListTabUiConfig"
import { TimelineTab } from "./timeLineTabUiConfig"
import { FeaturesTab } from "./featureTabUiConfig"
import { PanelsTab } from "./panelTabUiConfig"
import { EvidenceThemesTab } from "./evidenceThemesUiConfig"
import { DataPointsTab } from "./dataPointUiConfig"
import { CaseSearchTab } from "./caseSearchTabUiConfig"
import { QuestionPackTab } from "./questionpackTabUiConfig"
import { RetentionTabUiConfig } from "./retentionTabUiConfig"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import type { PanelsConfig } from "../shared/SharedPanelsTab"
import type { UserTag, AutoTag } from "@/utils/types"
import type { RetentionConfig } from "../shared/SharedRetentionTab"
import type { SearchIndexSchema } from "@/utils/caseSearchSchema"

const routeId = "brand-config"
const sessionKey = "brand-config-filter-storage"

interface EvidenceCount {
  name: string
  display?: string
}

export interface RecordListConfig {
  foldersViewEnabled: boolean
  summariser: { enabled: boolean }
  userTags: UserTag[]
  evidenceCounts: EvidenceCount[]
  autoTags: AutoTag[]
  showSearch?: boolean
}

interface PeriodEnumeration {
  name: string
  iconType: string
  icon: string
  color: string
  sequence: number
}

interface PeriodOption {
  name: string
  type: "manual" | "ioas"
  sequence: number
  enumeration: PeriodEnumeration[]
}

interface InstanceStyleValue {
  value: string
  color: string
  background: string
}

interface InstanceIcon {
  type: string
  position: string
  displayIfZero: boolean
  recordCount: string
  backgroundColor: string
  color: string
}

export interface TimelineConfig {
  general: {
    useUpper: boolean
    useLower: boolean
    useInstances: boolean
  }
  upper?: {
    allowManualPeriodDateAdjustment?: boolean
    allowManualPeriodOptionsAdjustment?: boolean
    periodNameSingular?: string
    periodNamePlural?: string
    periodOverlapMonths?: number
    periodOverlapMonthsToEOM?: boolean
    disableOverlapMonths?: boolean
    periodOptions?: PeriodOption[]
    icons?: {
      autoTag?: Record<
        string,
        Array<{
          value: string
          iconType: string
          icon: string
          color: string
        }>
      >
      userTag?: Record<string, unknown>
      evidenceCounts?: unknown[]
    }
  }
  lower?: {
    showRecordClusters: boolean
  }
  instances?: {
    showSequenceNumber: boolean
    instanceStyle: {
      defaultBackground: string
      defaultColour: string
      type: string
      instanceTag: string
      values?: InstanceStyleValue[]
    }
    instanceIcons: InstanceIcon[]
  }
}

export interface EvidenceThemesConfig {
  enableFilters?: boolean
  enableForUnknownClassifications?: boolean
  filters?: Array<{
    classification: string
    themes: Array<string | null>
  }>
  highlightColors?: { [x: string]: string }
  hiddenThemes?: { [x: string]: boolean }
}

interface CaseSearchField {
  defaultVisible: boolean
  displayName: string
  fieldPath: string
  filterable: boolean
  filterConfig: {
    description: string
    type: string
    order: number
    options?: Array<{
      name?: string
      value?: string | boolean
      label?: string
      diff?: number
    }>
    maxLength?: number
    placeholder?: string
    defaultValue?: string
  }
  order: number
  searchable: boolean
  sortable: boolean
  visible: boolean
  defaultSort?: string
  isCollection?: boolean
  sortConfig?: {
    asc: string
    desc: string
  }
}

interface DataPointLabel {
  position: number
  name: string
  themes: string[]
  showEvidenceThemes: boolean
}

interface DataPoint {
  usage: string
  labels: DataPointLabel[]
}
interface BrandUIConfig {
  brand: string
  concurrency: string
  value: {
    lens?: {
      concurrency: string
      recordList?: RecordListConfig
      timeline?: TimelineConfig
      evidenceThemes?: EvidenceThemesConfig
      caseSearch?: CaseSearchField[]
    }
    features?: Record<string, string>
    dataPoints?: DataPoint[]
    caseSearch?: CaseSearchField[]
    polling?: number[]
    questionPack?: {
      renderInputTextOnly: boolean
      css: string[]
      js: Array<{
        src: string
        children?: Array<{ src: string }>
      }>
    }
    recordList?: RecordListConfig
    properties?: {
      draftCheckbox?: { disabled: boolean }
      ioa?: { disabled: boolean }
    }
    tooltip?: Record<string, unknown>
    evidenceThemes?: EvidenceThemesConfig
    retention?: RetentionConfig
    timeline?: TimelineConfig
    panels?: PanelsConfig
    summaryNotes?: {
      export?: { enabled: boolean }
    }
  }
}

export interface LoaderData {
  loadedOrganisation: string
  projects: string[]
  loadedBrand?: string
  config?: BrandUIConfig | null
  searchSchema?: SearchIndexSchema | null
  searchSchemaError?: string
  evidenceCountData?: Array<{ name: string; criteriaLabel?: string }>
  userTagTypes?: string[]
  autoTagTypes?: string[]
  isIndex: boolean
  classificationMappings?: DocumentEvidenceCategory[]
  evidenceThemeData?: EvidenceTheme[]
  documentClassifications?: DocumentClassificationType[]
}

async function loader({ params }: { params: Record<string, string | undefined> }) {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>
  const loadedOrganisation = filters.parsed?.organisation ?? organisations[0]

  const projects = await fetchAPI<Project[]>(`/api/my/projects/${loadedOrganisation}`)
    .then(handleResponse)
    .then((projects) => projects.map((prj) => prj.name))

  const isIndex = params.brand == null

  if (isIndex) {
    return { loadedOrganisation, projects, isIndex: true }
  }

  const loadedBrand = params.brand

  if (loadedBrand == null || !projects.includes(loadedBrand)) {
    throw new Error("Brand not found")
  }

  const [
    config,
    schemaResult,
    evidenceCountData,
    tagTypeData,
    classificationMappings,
    evidenceThemeData,
    documentClassifications,
  ] = await Promise.all([
    fetchAPI<BrandUIConfig>(`/api/unityviewer/uiconfig/${loadedOrganisation}/${loadedBrand}`)
      .then(handleResponse)
      .catch(() => null),
    fetchAPI<SearchIndexSchema>(
      `/api/SearchConfig/${encodeURIComponent(loadedOrganisation)}/${encodeURIComponent(loadedBrand)}/CaseSearch`,
    )
      .then(handleResponse)
      .then((schema) => ({ schema, error: undefined }))
      .catch((error: unknown) => ({
        schema: null,
        error:
          error instanceof Response ? `HTTP ${error.status}` : error instanceof Error ? error.message : String(error),
      })),
    fetchAPI<Array<{ name: string; criteriaLabel?: string }>>(
      `/api/unityviewer/evidencecount/${encodeURIComponent(loadedOrganisation)}/${encodeURIComponent(loadedBrand)}`,
    )
      .then(handleResponse)
      .catch(() => []),
    fetchAPI<Array<{ name: string; tagRestriction: string }>>(
      `/api/unityviewer/tagtype/${encodeURIComponent(loadedOrganisation)}/${encodeURIComponent(loadedBrand)}`,
    )
      .then(handleResponse)
      .catch(() => []),

    fetchAPI<DocumentEvidenceCategory[]>(
      `/api/unityviewer/documentclassificationevidencetheme/${loadedOrganisation}/${loadedBrand}`,
    )
      .then(handleResponse)
      .catch(() => []),

    fetchAPI<EvidenceTheme[]>(`/api/unityviewer/evidencetheme/${loadedOrganisation}/${loadedBrand}`)
      .then(handleResponse)
      .catch(() => []),

    fetchAPI<DocumentClassificationType[]>(
      `/api/unityviewer/documentclassification/${loadedOrganisation}/${loadedBrand}`,
    )
      .then(handleResponse)
      .catch(() => []),
  ])

  const userTagTypes = tagTypeData
    .filter((tag) => tag.tagRestriction.toLowerCase() !== "autotag")
    .map((tag) => tag.name)

  const autoTagTypes = tagTypeData
    .filter((tag) => tag.tagRestriction.toLowerCase() === "autotag")
    .map((tag) => tag.name)

  return {
    loadedOrganisation,
    projects,
    loadedBrand,
    config,
    searchSchema: schemaResult.schema,
    searchSchemaError: schemaResult.error,
    evidenceCountData,
    userTagTypes,
    autoTagTypes,
    isIndex: false,
    classificationMappings,
    evidenceThemeData,
    documentClassifications,
  }
}
async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  const actionType = formData.get("action") as string
  const organisation = formData.get("organisation") as string
  const brand = formData.get("brand") as string
  const project = formData.get("project") as string
  const jsonPath = formData.get("jsonPath") as string
  const value = formData.get("value") as string
  const concurrency = formData.get("concurrency") as string

  if (organisation == null) throw new Error("Missing organisation")

  const targetBrand = brand ?? project
  if (targetBrand.length === 0) throw new Error("Missing brand/project")

  let errorMessage: string | undefined

  try {
    switch (actionType) {
      case "DELETE_BRAND": {
        await fetchAPI(`/api/unityviewer/uiconfig/${organisation}/${targetBrand}`, {
          method: "PUT",
          body: {
            jsonPath: null,
            concurrency: concurrency ?? "",
            applyOperation: "Delete",
          },
        }).then(throwIfNotOk)
        return { action: actionType, success: true }
      }

      case "ADD_SECTION": {
        if (value == null) {
          throw new Error("Missing value field")
        }

        await fetchAPI(`/api/unityviewer/uiconfig/${organisation}/${targetBrand}`, {
          method: "POST",
          body: {
            value: JSON.parse(value),
            concurrency: concurrency ?? "",
          },
        }).then(throwIfNotOk)

        return { action: actionType, success: true }
      }

      case "SAVE_SECTION": {
        if (jsonPath == null || value == null) {
          throw new Error("Missing required fields")
        }

        await fetchAPI(`/api/unityviewer/uiconfig/${organisation}/${targetBrand}`, {
          method: "PATCH",
          body: {
            jsonPath,
            value: JSON.parse(value),
            concurrency: concurrency ?? "",
          },
        }).then(throwIfNotOk)
        toast.success("Configuration saved successfully.")

        return { action: actionType, success: true }
      }

      case "REMOVE_SECTION": {
        if (jsonPath == null) {
          throw new Error("Missing jsonPath")
        }

        await fetchAPI(`/api/unityviewer/uiconfig/${organisation}/${targetBrand}`, {
          method: "PUT",
          body: {
            jsonPath,
            concurrency: concurrency ?? "",
            applyOperation: "Remove",
          },
        }).then(throwIfNotOk)

        return { action: actionType, success: true }
      }

      default:
        throw new Error(`Unknown action: ${actionType}`)
    }
  } catch (error) {
    console.error(`Error in ${actionType}:`, error)
    errorMessage = error instanceof Error ? error.message : `Error performing ${actionType}`
    return { action: actionType, success: false, errorMessage }
  }
}

function BrandList() {
  const data = useLoaderData<typeof loader>()
  const navigate = useNavigate()
  const fetcher = useFetcher<ActionResponse>()
  const [deletingBrand, setDeletingBrand] = React.useState<string | null>(null)

  const handleConfigure = (brand: string) => {
    navigate(brand)
  }

  return (
    <>
      <div className="h-full overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead></TableHead>
              <TableHead className="text-left">Brand Name</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.projects.length > 0 ? (
              data.projects.map((brand, index) => (
                <TableRow key={brand} className="text-sm">
                  <TableCell>{index + 1}.</TableCell>
                  <TableCell>{brand}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleConfigure(brand)}
                        variant="outline"
                        size="icon"
                        aria-label="Configure Brand"
                      >
                        <SettingsIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => setDeletingBrand(brand)}
                        variant="ghost"
                        size="icon"
                        className="text-red-600 hover:bg-red-50"
                        aria-label="Delete Brand Config"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} className="py-4 text-center text-muted-foreground">
                  No brands found for this organisation.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deletingBrand !== null} onOpenChange={(open) => !open && setDeletingBrand(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Brand Configuration?</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete the entire configuration for{" "}
              <span className="font-semibold">{deletingBrand}</span>?
            </p>
            <p className="text-sm text-muted-foreground">
              This will remove all brand-level config and force the system to use lens configuration as the source of
              truth.
            </p>
            <p className="text-sm font-medium text-red-600">This action cannot be undone.</p>
            {fetcher.data?.errorMessage != null && <p className="text-sm text-red-600">{fetcher.data.errorMessage}</p>}
          </div>

          <div className="flex justify-end gap-3 border-t pt-4">
            <DialogClose asChild>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={() => {
                if (deletingBrand === null) return

                const formData = new FormData()
                formData.append("action", "DELETE_BRAND")
                formData.append("organisation", data.loadedOrganisation)
                formData.append("brand", deletingBrand)
                formData.append("concurrency", data.config?.concurrency ?? "")

                fetcher.submit(formData, { method: "post" })
                setDeletingBrand(null)
              }}
              variant="destructive"
              disabled={fetcher.state !== "idle"}
              type="button"
            >
              {fetcher.state !== "idle" && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />}
              Delete Configuration
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function PropertiesTab({
  config,
  data,
}: {
  config: BrandUIConfig["value"]["properties"] | undefined
  data: LoaderData
}) {
  const [properties, setProperties] = React.useState(config ?? {})
  const fetcher = useFetcher<ActionResponse>()

  if (config == null) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <p className="mb-4 text-muted-foreground">No properties configuration found</p>
            <fetcher.Form method="post">
              <input type="hidden" name="action" value="ADD_SECTION" />
              <input type="hidden" name="organisation" value={data.loadedOrganisation} />
              <input type="hidden" name="brand" value={data.loadedBrand} />
              <input type="hidden" name="jsonPath" value="$.properties" />
              <input
                type="hidden"
                name="value"
                value={JSON.stringify({
                  ...(data.config?.value ?? {}),
                  properties: {
                    draftCheckbox: { disabled: false },
                    ioa: { disabled: false },
                  },
                })}
              />
              <input type="hidden" name="concurrency" value={data.config?.concurrency ?? ""} />
              <Button type="submit" disabled={fetcher.state !== "idle"}>
                {fetcher.state !== "idle" && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />}
                <PlusIcon className="mr-2 h-4 w-4" />
                Add Properties Section
              </Button>
            </fetcher.Form>
            {fetcher.data?.errorMessage != null && (
              <p className="mt-4 text-sm text-red-600">{fetcher.data.errorMessage}</p>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="mx-auto w-full space-y-4">
          <div className="rounded-lg border p-4">
            <h3 className="mb-4 text-lg font-semibold">Draft Checkbox</h3>
            <div className="flex items-center space-x-3">
              <Checkbox
                id="draftCheckboxDisabled"
                checked={properties.draftCheckbox?.disabled ?? false}
                onCheckedChange={(checked) =>
                  setProperties((prev) => ({
                    ...prev,
                    draftCheckbox: { disabled: checked === true },
                  }))
                }
              />
              <label htmlFor="draftCheckboxDisabled" className="cursor-pointer text-sm">
                Disabled
              </label>
            </div>
          </div>

          <div className="rounded-lg border p-4">
            <h3 className="mb-4 text-lg font-semibold">IOA (Instance of Advice)</h3>
            <div className="flex items-center space-x-3">
              <Checkbox
                id="ioaDisabled"
                checked={properties.ioa?.disabled ?? false}
                onCheckedChange={(checked) =>
                  setProperties((prev) => ({
                    ...prev,
                    ioa: { disabled: checked === true },
                  }))
                }
              />
              <label htmlFor="ioaDisabled" className="cursor-pointer text-sm">
                Disabled
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 border-t p-4">
        <div className="mx-auto flex w-full items-center gap-3">
          <fetcher.Form method="post" className="flex-1">
            <input type="hidden" name="action" value="SAVE_SECTION" />
            <input type="hidden" name="organisation" value={data.loadedOrganisation} />
            <input type="hidden" name="brand" value={data.loadedBrand} />
            <input type="hidden" name="jsonPath" value="$.properties" />
            <input type="hidden" name="value" value={JSON.stringify(properties)} />
            <input type="hidden" name="concurrency" value={data.config?.concurrency ?? ""} />
            <Button type="submit" disabled={fetcher.state !== "idle"} className="w-full bg-info hover:bg-info/90">
              {fetcher.state !== "idle" && fetcher.formData?.get("action") === "SAVE_SECTION" && (
                <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              <SaveIcon className="h-4 w-4" />
              Save Properties
            </Button>
          </fetcher.Form>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                disabled={fetcher.state !== "idle"}
              >
                {fetcher.state !== "idle" && fetcher.formData?.get("action") === "REMOVE_SECTION" && (
                  <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
                )}
                <XCircleIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Remove Section</span>
                <span className="sm:hidden">Remove</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent side="top" className="w-80">
              <div className="space-y-3">
                <p className="text-sm font-medium">Remove Properties Configuration?</p>
                <p className="text-sm text-muted-foreground">
                  This will remove the entire properties configuration from this level.
                </p>
                <p className="text-sm font-medium text-red-600">This action cannot be undone.</p>
                <div className="flex items-center justify-end gap-3">
                  <PopoverClose asChild>
                    <Button variant="outline" size="sm">
                      Cancel
                    </Button>
                  </PopoverClose>
                  <PopoverClose asChild>
                    <fetcher.Form method="post">
                      <input type="hidden" name="action" value="REMOVE_SECTION" />
                      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
                      <input type="hidden" name="brand" value={data.loadedBrand} />
                      <input type="hidden" name="jsonPath" value="$.properties" />
                      <input type="hidden" name="concurrency" value={data.config?.concurrency ?? ""} />
                      <Button type="submit" variant="destructive" size="sm" disabled={fetcher.state !== "idle"}>
                        {fetcher.state !== "idle" && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />}
                        Remove
                      </Button>
                    </fetcher.Form>
                  </PopoverClose>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  )
}

function SummaryNotesTab({
  config,
  data,
}: {
  config: BrandUIConfig["value"]["summaryNotes"] | undefined
  data: LoaderData
}) {
  const [summaryNotes, setSummaryNotes] = React.useState(config ?? {})
  const fetcher = useFetcher<ActionResponse>()

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="mx-auto w-full rounded-lg border p-4">
          <h3 className="mb-4 text-lg font-semibold">Export Settings</h3>
          <div className="flex items-center space-x-3">
            <Checkbox
              id="exportEnabled"
              checked={summaryNotes.export?.enabled ?? false}
              onCheckedChange={(checked) =>
                setSummaryNotes((prev) => ({
                  ...prev,
                  export: { enabled: checked === true },
                }))
              }
            />
            <label htmlFor="exportEnabled" className="cursor-pointer text-sm">
              Enable Export
            </label>
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 border-t p-4">
        <div className="mx-auto flex w-full items-center gap-3">
          <fetcher.Form method="post" className="flex-1">
            <input type="hidden" name="action" value="SAVE_SECTION" />
            <input type="hidden" name="organisation" value={data.loadedOrganisation} />
            <input type="hidden" name="brand" value={data.loadedBrand} />
            <input type="hidden" name="jsonPath" value="$.summaryNotes" />
            <input type="hidden" name="value" value={JSON.stringify(summaryNotes)} />
            <input type="hidden" name="concurrency" value={data.config?.concurrency ?? ""} />
            <Button type="submit" disabled={fetcher.state !== "idle"} className="w-full bg-info hover:bg-info/90">
              {fetcher.state !== "idle" && fetcher.formData?.get("action") === "SAVE_SECTION" && (
                <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              <SaveIcon className="h-4 w-4" />
              Save Summary Notes
            </Button>
          </fetcher.Form>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                disabled={fetcher.state !== "idle"}
              >
                {fetcher.state !== "idle" && fetcher.formData?.get("action") === "REMOVE_SECTION" && (
                  <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
                )}
                <XCircleIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Remove Section</span>
                <span className="sm:hidden">Remove</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent side="top" className="w-80">
              <div className="space-y-3">
                <p className="text-sm font-medium">Remove Summary Notes Configuration?</p>
                <p className="text-sm text-muted-foreground">
                  This will remove the entire summary notes configuration from this level.
                </p>
                <p className="text-sm font-medium text-red-600">This action cannot be undone.</p>
                <div className="flex items-center justify-end gap-3">
                  <PopoverClose asChild>
                    <Button variant="outline" size="sm">
                      Cancel
                    </Button>
                  </PopoverClose>
                  <PopoverClose asChild>
                    <fetcher.Form method="post">
                      <input type="hidden" name="action" value="REMOVE_SECTION" />
                      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
                      <input type="hidden" name="brand" value={data.loadedBrand} />
                      <input type="hidden" name="jsonPath" value="$.summaryNotes" />
                      <input type="hidden" name="concurrency" value={data.config?.concurrency ?? ""} />
                      <Button type="submit" variant="destructive" size="sm" disabled={fetcher.state !== "idle"}>
                        {fetcher.state !== "idle" && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />}
                        Remove
                      </Button>
                    </fetcher.Form>
                  </PopoverClose>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  )
}

function PollingTab({ config, data }: { config: number[] | undefined; data: LoaderData }) {
  const [intervals, setIntervals] = React.useState<number[]>(config ?? [])
  const [showAddDialog, setShowAddDialog] = React.useState(false)
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null)
  const [intervalValue, setIntervalValue] = React.useState("")

  const fetcher = useFetcher<ActionResponse>()

  // Sort intervals in ascending order for display
  const sortedIntervals = React.useMemo(() => {
    return [...intervals].sort((a, b) => a - b)
  }, [intervals])

  const addInterval = () => {
    const interval = parseInt(intervalValue)
    if (isNaN(interval) || interval <= 0) return

    setIntervals((prev) => [...prev, interval])
    setIntervalValue("")
    setShowAddDialog(false)
  }

  const updateInterval = () => {
    if (editingIndex === null) return
    const interval = parseInt(intervalValue)
    if (isNaN(interval) || interval <= 0) return

    const originalInterval = sortedIntervals[editingIndex]
    setIntervals((prev) => prev.map((val) => (val === originalInterval ? interval : val)))
    setIntervalValue("")
    setEditingIndex(null)
  }

  const removeInterval = (interval: number) => {
    setIntervals((prev) => {
      const index = prev.indexOf(interval)
      if (index === -1) return prev
      return prev.filter((_, i) => i !== index)
    })
  }

  const openEditDialog = (index: number) => {
    setEditingIndex(index)
    setIntervalValue(sortedIntervals[index].toString())
  }

  if (config == null) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <p className="mb-4 text-muted-foreground">No polling configuration found</p>
            <fetcher.Form method="post">
              <input type="hidden" name="action" value="ADD_SECTION" />
              <input type="hidden" name="organisation" value={data.loadedOrganisation} />
              <input type="hidden" name="brand" value={data.loadedBrand} />
              <input type="hidden" name="jsonPath" value="$.polling" />
              <input
                type="hidden"
                name="value"
                value={JSON.stringify({
                  ...(data.config?.value ?? {}),
                  polling: [],
                })}
              />
              <input type="hidden" name="concurrency" value={data.config?.concurrency ?? ""} />
              <Button type="submit" disabled={fetcher.state !== "idle"}>
                {fetcher.state !== "idle" && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />}
                <PlusIcon className="mr-2 h-4 w-4" />
                Add Polling Section
              </Button>
            </fetcher.Form>
            {fetcher.data?.errorMessage != null && (
              <p className="mt-4 text-sm text-red-600">{fetcher.data.errorMessage}</p>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="mx-auto w-full space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Polling Intervals (seconds)</h2>
            <Button onClick={() => setShowAddDialog(true)} variant="outline" size="sm">
              <PlusIcon className="mr-2 h-4 w-4" />
              Add Interval
            </Button>
          </div>
          {sortedIntervals.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-border py-16 text-center text-muted-foreground">
              <p className="text-lg">No polling intervals configured</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sortedIntervals.map((interval, idx) => (
                <div key={`${interval}-${idx}`} className="flex items-center justify-between rounded-lg border p-4">
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-semibold text-foreground">Interval {idx + 1}</span>
                    <span className="text-2xl font-semibold text-foreground">{interval}</span>
                    <span className="text-sm text-muted-foreground">seconds</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openEditDialog(idx)}
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground hover:bg-secondary hover:text-foreground"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50">
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent side="top" className="w-80">
                        <div className="space-y-3">
                          <p className="text-sm font-medium">Delete Polling Interval?</p>
                          <p className="text-sm text-muted-foreground">
                            This will delete the {interval} second interval.
                          </p>
                          <p className="text-sm font-medium text-red-600">This action cannot be undone.</p>
                          <div className="flex items-center justify-end gap-3">
                            <PopoverClose asChild>
                              <Button variant="outline" size="sm">
                                Cancel
                              </Button>
                            </PopoverClose>
                            <PopoverClose asChild>
                              <Button onClick={() => removeInterval(interval)} variant="destructive" size="sm">
                                Delete
                              </Button>
                            </PopoverClose>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex-shrink-0 border-t p-4">
        <div className="mx-auto flex w-full gap-3">
          <fetcher.Form method="post" className="flex-1">
            <input type="hidden" name="action" value="SAVE_SECTION" />
            <input type="hidden" name="organisation" value={data.loadedOrganisation} />
            <input type="hidden" name="brand" value={data.loadedBrand} />
            <input type="hidden" name="jsonPath" value="$.polling" />
            <input type="hidden" name="value" value={JSON.stringify(intervals)} />
            <input type="hidden" name="concurrency" value={data.config?.concurrency ?? ""} />
            <Button type="submit" disabled={fetcher.state !== "idle"} className="w-full bg-info hover:bg-info/90">
              {fetcher.state !== "idle" && fetcher.formData?.get("action") === "SAVE_SECTION" && (
                <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              <SaveIcon className="h-4 w-4" />
              Save Polling
            </Button>
          </fetcher.Form>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                disabled={fetcher.state !== "idle"}
              >
                {fetcher.state !== "idle" && fetcher.formData?.get("action") === "REMOVE_SECTION" && (
                  <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
                )}
                <XCircleIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Remove Section</span>
                <span className="sm:hidden">Remove</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent side="top" className="w-80">
              <div className="space-y-3">
                <p className="text-sm font-medium">Remove Polling Configuration?</p>
                <p className="text-sm text-muted-foreground">
                  This will remove the entire polling configuration from this level.
                </p>
                <p className="text-sm font-medium text-red-600">This action cannot be undone.</p>
                <div className="flex items-center justify-end gap-3">
                  <PopoverClose asChild>
                    <Button variant="outline" size="sm">
                      Cancel
                    </Button>
                  </PopoverClose>
                  <PopoverClose asChild>
                    <fetcher.Form method="post">
                      <input type="hidden" name="action" value="REMOVE_SECTION" />
                      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
                      <input type="hidden" name="brand" value={data.loadedBrand} />
                      <input type="hidden" name="jsonPath" value="$.polling" />
                      <input type="hidden" name="concurrency" value={data.config?.concurrency ?? ""} />
                      <Button type="submit" variant="destructive" size="sm" disabled={fetcher.state !== "idle"}>
                        {fetcher.state !== "idle" && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />}
                        Remove
                      </Button>
                    </fetcher.Form>
                  </PopoverClose>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Add/Edit Interval Dialog */}
      <Dialog
        open={showAddDialog || editingIndex !== null}
        onOpenChange={(open) => {
          if (!open) {
            setShowAddDialog(false)
            setEditingIndex(null)
            setIntervalValue("")
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingIndex !== null ? "Edit Polling Interval" : "Add Polling Interval"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium">Interval (seconds) *</label>
              <Input
                type="number"
                value={intervalValue}
                onChange={(e) => setIntervalValue(e.target.value)}
                placeholder="e.g., 2, 15, 30"
                min="1"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t pt-4">
            <DialogClose asChild>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={editingIndex !== null ? updateInterval : addInterval}
              disabled={intervalValue.length === 0 || isNaN(parseInt(intervalValue)) || parseInt(intervalValue) <= 0}
              type="button"
            >
              {editingIndex !== null ? "Save" : "Add"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function ConfigurationEditor() {
  const data = useLoaderData<typeof loader>()
  const [activeTab, setActiveTab] = useReducer((state: string, tab: string) => tab, "features")

  const originalConfig = useMemo(() => data.config?.value ?? {}, [data.config?.value])

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="mb-2 flex-shrink-0 border-b border-border px-4">
        <div className="flex space-x-4 overflow-x-auto lg:space-x-8">
          {[
            { id: "features", label: "Features" },
            { id: "recordList", label: "Record List" },
            { id: "panels", label: "Panels" },
            { id: "timeline", label: "Timeline" },
            { id: "evidenceThemes", label: "Evidence Themes" },
            { id: "retention", label: "Retention" },
            { id: "dataPoints", label: "Data Points" },
            { id: "caseSearch", label: "Case Search" },
            { id: "properties", label: "Properties" },
            { id: "summaryNotes", label: "Summary Notes" },
            { id: "questionPack", label: "Question Pack" },
            { id: "polling", label: "Polling" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`border-b-2 px-1 pb-2 text-sm font-medium whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1" key={`${data.loadedBrand}-${data.config?.concurrency ?? ""}`}>
        {activeTab === "features" && <FeaturesTab config={originalConfig.features} data={data} />}
        {activeTab === "recordList" && <RecordListTab config={originalConfig.recordList} data={data} />}
        {activeTab === "panels" && <PanelsTab config={originalConfig.panels} data={data} />}
        {activeTab === "timeline" && <TimelineTab config={originalConfig.timeline} data={data} />}
        {activeTab === "evidenceThemes" && <EvidenceThemesTab config={originalConfig.evidenceThemes} data={data} />}
        {activeTab === "retention" && <RetentionTabUiConfig config={originalConfig.retention} data={data} />}
        {activeTab === "dataPoints" && <DataPointsTab config={originalConfig.dataPoints} data={data} />}
        {activeTab === "caseSearch" && <CaseSearchTab config={originalConfig.caseSearch} data={data} />}
        {activeTab === "properties" && <PropertiesTab config={originalConfig.properties} data={data} />}
        {activeTab === "summaryNotes" && <SummaryNotesTab config={originalConfig.summaryNotes} data={data} />}
        {activeTab === "questionPack" && <QuestionPackTab config={originalConfig.questionPack} data={data} />}
        {activeTab === "polling" && <PollingTab config={originalConfig.polling} data={data} />}
      </div>
    </div>
  )
}

export function BrandConfigManager() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useRouteLoaderData<typeof loader>(routeId)
  const matches = useMatches()

  const onBeforeSubmit = useCallback<OnBeforeSubmit>((formData, reason) => {
    if (reason.type !== "field") return
    if (reason.name === "organisation") formData.delete("project")
  }, [])

  if (rootData == null || data == null) return null

  const isOnBrandList = !matches.some((m) => m.params.brand != null)

  return (
    <>
      {isOnBrandList && rootData.organisations.length > 1 && (
        <div className="mb-4 flex items-center gap-4">
          <FiltersWrapper submitOnChange canReset={false} sessionKey={sessionKey} onBeforeSubmit={onBeforeSubmit}>
            <FiltersWrapper.Select
              name="organisation"
              label="Organisation"
              defaultValue={data.loadedOrganisation}
              items={rootData.organisations}
            />
          </FiltersWrapper>
        </div>
      )}
      <Outlet />
    </>
  )
}

BrandConfigManager.loader = loader
BrandConfigManager.action = action
BrandList.action = action
ConfigurationEditor.action = action
BrandConfigManager.BrandList = BrandList
BrandConfigManager.ConfigurationEditor = ConfigurationEditor
BrandConfigManager.routeId = routeId
